/**
 * Capacitor JS bridges — Phase 1 incr. 20 (bootstrap) + Phase 7.1
 * (adoption `@capacitor/core` + CLI).
 *
 * Wrappers TypeScript fins autour du runtime Capacitor 6+. La source
 * de vérité est le global **`window.Capacitor`** :
 *
 *   - En **natif** (iOS / Android), c'est le runtime de
 *     `@capacitor/core` — embarqué par le shell natif via la WebView —
 *     qui pose ce global. Nos wrappers le consomment tel quel.
 *   - En **test**, `_setCapacitorForTests` l'injecte pour simuler le
 *     natif.
 *   - En **web pur**, le global est absent → détection retombe sur
 *     les valeurs par défaut (`'web'` / `false`).
 *
 * Choix volontaire : on n'importe **pas** statiquement l'objet
 * `Capacitor` de `@capacitor/core` dans la bundle web. En natif il
 * n'apporterait rien de plus que le global (qu'il pose lui-même), et
 * en web il ne ferait que renvoyer `'web'`/`false` — soit ~3 KB gzip
 * de poids mort pour 100 % des utilisateurs web. Le package
 * `@capacitor/core` reste néanmoins une dépendance : il est requis
 * par la CLI (`npx cap sync`, lecture de `capacitor.config.ts`) et
 * comme peer des futurs plugins (7.3 : `@capacitor/status-bar`,
 * `@capacitor/haptics`).
 *
 * Garanties :
 *   - **No-op gracieux côté web** : si aucun plugin n'est disponible,
 *     les méthodes retournent `false` / `undefined` sans throw.
 *   - **Détection runtime**, pas de check à la build : la même bundle
 *     tourne en navigateur, en Capacitor iOS et Capacitor Android.
 *   - En 7.1 **aucun package plugin** n'est installé → `getPlugin`
 *     renvoie `null` partout, y compris en natif. Plugins en 7.3.
 */

/**
 * Surface minimale `window.Capacitor` qu'on consomme. Volontairement
 * permissive (les plugins exposent des API hétérogènes — on les
 * raffine dans chaque wrapper).
 */
export interface CapacitorRuntime {
  isNativePlatform?(): boolean;
  isPluginAvailable?(name: string): boolean;
  getPlatform?(): 'web' | 'ios' | 'android';
  Plugins?: Record<string, unknown>;
}

/**
 * Lit le runtime Capacitor (`window.Capacitor`) de manière typée s'il
 * est présent. Posé par le runtime natif de `@capacitor/core` (ou par
 * `_setCapacitorForTests`). Absent en web pur → `undefined`.
 */
function getCapacitor(): CapacitorRuntime | undefined {
  if (typeof window === 'undefined') return undefined;
  const cap = (window as unknown as { Capacitor?: CapacitorRuntime }).Capacitor;
  return cap ?? undefined;
}

/**
 * Vrai si l'app tourne dans Capacitor (iOS ou Android natif).
 * Faux en navigateur web standard. Utilisé pour décider d'activer
 * des features natives (status bar custom, haptics, etc.).
 */
export function isNative(): boolean {
  const cap = getCapacitor();
  if (!cap) return false;
  return typeof cap.isNativePlatform === 'function' ? cap.isNativePlatform() : false;
}

/**
 * Récupère un plugin Capacitor par nom si disponible. Retourne `null`
 * si l'app n'est pas native ou si le plugin n'est pas installé.
 *
 * Les plugins suivent l'API Capacitor 4+ : objets exposant des
 * méthodes async. Le typage `T` est laissé au caller (chaque wrapper
 * définit son contrat).
 */
export function getPlugin<T>(name: string): T | null {
  const cap = getCapacitor();
  if (!cap) return null;
  if (!isNative()) return null;
  if (typeof cap.isPluginAvailable === 'function' && !cap.isPluginAvailable(name)) {
    return null;
  }
  const plugin = cap.Plugins?.[name];
  return (plugin as T | undefined) ?? null;
}

/**
 * Plate-forme courante : `'web' | 'ios' | 'android'`. Web par défaut.
 */
export function getPlatform(): 'web' | 'ios' | 'android' {
  const cap = getCapacitor();
  if (!cap || typeof cap.getPlatform !== 'function') return 'web';
  return cap.getPlatform();
}

/** @internal pour les tests : permet d'injecter un faux runtime. */
export function _setCapacitorForTests(fake: CapacitorRuntime | undefined): void {
  if (typeof window === 'undefined') return;
  if (fake === undefined) {
    delete (window as unknown as { Capacitor?: CapacitorRuntime }).Capacitor;
  } else {
    (window as unknown as { Capacitor?: CapacitorRuntime }).Capacitor = fake;
  }
}
